import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import {
  Shield,
  Users,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Lock,
  Eye,
  FileText,
  Clock,
  Database,
  Server,
  Key,
  ShieldCheck,
  UserCheck,
  Activity,
  ChevronDown,
  ChevronUp,
  Folder,
  Upload,
} from 'lucide-react';

// Compliance framework definitions
const complianceFrameworks = [
  {
    id: 'cjis',
    name: 'CJIS Security Policy',
    description: 'Criminal Justice Information Services',
    controls: [
      { id: 'cjis-1', name: 'Access Control', status: 'compliant', details: 'Role-based access implemented' },
      { id: 'cjis-2', name: 'Audit & Accountability', status: 'compliant', details: 'Full audit trail via CustodyChainEvents' },
      { id: 'cjis-3', name: 'Media Protection', status: 'compliant', details: 'WORM immutability policies on storage' },
      { id: 'cjis-4', name: 'Physical Protection', status: 'compliant', details: 'Azure datacenter SOC 2 certified' },
      { id: 'cjis-5', name: 'Personnel Security', status: 'inprogress', details: 'Background checks via Entra ID integration' },
      { id: 'cjis-6', name: 'Encryption', status: 'compliant', details: 'TLS 1.3 in transit, AES-256 at rest' },
      { id: 'cjis-7', name: 'Authentication', status: 'compliant', details: 'Entra ID with MFA capability' },
    ],
  },
  {
    id: 'fedramp',
    name: 'FedRAMP',
    description: 'Federal Risk and Authorization Management Program',
    controls: [
      { id: 'fed-1', name: 'Continuous Monitoring', status: 'compliant', details: 'Azure Monitor & Log Analytics' },
      { id: 'fed-2', name: 'Incident Response', status: 'inprogress', details: 'Azure Security Center alerts configured' },
      { id: 'fed-3', name: 'Vulnerability Scanning', status: 'compliant', details: 'Azure Defender enabled' },
      { id: 'fed-4', name: 'Configuration Management', status: 'compliant', details: 'Infrastructure as Code (Bicep)' },
      { id: 'fed-5', name: 'Data Integrity', status: 'compliant', details: 'SHA-256 hash verification' },
    ],
  },
  {
    id: 'nist',
    name: 'NIST 800-53',
    description: 'Security and Privacy Controls',
    controls: [
      { id: 'nist-1', name: 'AC - Access Control', status: 'compliant', details: 'RBAC with 5 defined roles' },
      { id: 'nist-2', name: 'AU - Audit & Accountability', status: 'compliant', details: 'Immutable audit logs' },
      { id: 'nist-3', name: 'IA - Identification & Auth', status: 'compliant', details: 'Entra ID SSO' },
      { id: 'nist-4', name: 'SC - System Protection', status: 'compliant', details: 'Network isolation, encryption' },
      { id: 'nist-5', name: 'SI - System Integrity', status: 'compliant', details: 'Hash verification on upload' },
      { id: 'nist-6', name: 'MP - Media Protection', status: 'compliant', details: 'WORM storage policies' },
    ],
  },
];

// Role definitions with permissions
const roleDefinitions = [
  {
    role: 'SystemAdmin',
    description: 'Full system access, user management, compliance oversight',
    permissions: ['All permissions', 'User management', 'System configuration', 'Compliance reports'],
    color: 'bg-red-100 text-red-800',
  },
  {
    role: 'CaseManager',
    description: 'Case and evidence workflow management',
    permissions: ['View all cases', 'Manage evidence', 'Approve submissions', 'Generate reports'],
    color: 'bg-purple-100 text-purple-800',
  },
  {
    role: 'Attorney',
    description: 'Legal case access and court filing',
    permissions: ['View assigned cases', 'File evidence', 'Discovery sharing', 'Create exhibits'],
    color: 'bg-blue-100 text-blue-800',
  },
  {
    role: 'LawEnforcement',
    description: 'Evidence submission and case investigation',
    permissions: ['Upload evidence', 'View assigned cases', 'Chain of custody', 'Request redactions'],
    color: 'bg-green-100 text-green-800',
  },
  {
    role: 'PublicSubmitter',
    description: 'Limited evidence submission access',
    permissions: ['Submit evidence', 'View own submissions', 'Track status'],
    color: 'bg-gray-100 text-gray-800',
  },
];

export default function Compliance() {
  const { user } = useAuth();
  const [expandedFramework, setExpandedFramework] = useState<string | null>('cjis');
  const [activeTab, setActiveTab] = useState<'overview' | 'rbac' | 'audit' | 'health'>('overview');

  // Get audit/custody events
  const { data: custodyEvents } = useQuery({
    queryKey: ['custody-events-recent'],
    queryFn: () => apiClient.getCustodyEvents({}),
  });

  // Get custody stats
  const { data: custodyStats } = useQuery({
    queryKey: ['custody-stats'],
    queryFn: () => apiClient.getCustodyStats(),
  });

  // Get case count
  const { data: cases } = useQuery({
    queryKey: ['cases'],
    queryFn: () => apiClient.getCases(),
  });

  // Role check
  if (!user || !['CaseManager', 'SystemAdmin'].includes(user.role)) {
    return (
      <div className="p-8 text-center text-gray-500">
        You don't have permission to access Compliance Dashboard.
      </div>
    );
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'compliant':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'inprogress':
        return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
      default:
        return <XCircle className="w-5 h-5 text-red-600" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'compliant':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Compliant</span>;
      case 'inprogress':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">In Progress</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Non-Compliant</span>;
    }
  };

  const calculateComplianceScore = (framework: typeof complianceFrameworks[0]) => {
    const compliant = framework.controls.filter(c => c.status === 'compliant').length;
    return Math.round((compliant / framework.controls.length) * 100);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Shield className="w-7 h-7 text-indigo-600" />
          Security & Compliance Dashboard
        </h1>
        <p className="text-gray-600 mt-1">
          Role-based access controls, compliance status, and audit overview
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-8">
          {[
            { id: 'overview', label: 'Compliance Overview', icon: ShieldCheck },
            { id: 'rbac', label: 'Access Controls', icon: Users },
            { id: 'audit', label: 'Security Events', icon: Activity },
            { id: 'health', label: 'System Health', icon: Server },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 pb-3 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Compliance Summary Cards */}
          <div className="grid grid-cols-3 gap-6">
            {complianceFrameworks.map(framework => {
              const score = calculateComplianceScore(framework);
              return (
                <div key={framework.id} className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-lg">{framework.name}</h3>
                      <p className="text-sm text-gray-500">{framework.description}</p>
                    </div>
                    <div className={`text-3xl font-bold ${score >= 80 ? 'text-green-600' : score >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {score}%
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${score >= 80 ? 'bg-green-600' : score >= 60 ? 'bg-yellow-600' : 'bg-red-600'}`}
                      style={{ width: `${score}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    {framework.controls.filter(c => c.status === 'compliant').length} of {framework.controls.length} controls compliant
                  </p>
                </div>
              );
            })}
          </div>

          {/* Detailed Frameworks */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h2 className="font-semibold text-lg">Compliance Control Details</h2>
            </div>
            {complianceFrameworks.map(framework => (
              <div key={framework.id} className="border-b last:border-b-0">
                <button
                  onClick={() => setExpandedFramework(expandedFramework === framework.id ? null : framework.id)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-indigo-600" />
                    <div className="text-left">
                      <div className="font-medium">{framework.name}</div>
                      <div className="text-sm text-gray-500">{framework.controls.length} controls</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      calculateComplianceScore(framework) >= 80 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {calculateComplianceScore(framework)}% Compliant
                    </span>
                    {expandedFramework === framework.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>
                
                {expandedFramework === framework.id && (
                  <div className="px-4 pb-4">
                    <table className="w-full">
                      <thead>
                        <tr className="text-sm text-gray-500">
                          <th className="text-left py-2 px-3">Control</th>
                          <th className="text-left py-2 px-3">Status</th>
                          <th className="text-left py-2 px-3">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {framework.controls.map(control => (
                          <tr key={control.id} className="border-t">
                            <td className="py-3 px-3">
                              <div className="flex items-center gap-2">
                                {getStatusIcon(control.status)}
                                <span className="font-medium">{control.name}</span>
                              </div>
                            </td>
                            <td className="py-3 px-3">{getStatusBadge(control.status)}</td>
                            <td className="py-3 px-3 text-gray-600">{control.details}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* RBAC Tab */}
      {activeTab === 'rbac' && (
        <div className="space-y-6">
          {/* Role Overview */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h2 className="font-semibold text-lg flex items-center gap-2">
                <UserCheck className="w-5 h-5" />
                Role-Based Access Control (RBAC)
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                DEMS implements 5 distinct roles with principle of least privilege
              </p>
            </div>
            <div className="divide-y">
              {roleDefinitions.map(role => (
                <div key={role.role} className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${role.color}`}>
                          {role.role}
                        </span>
                      </div>
                      <p className="text-gray-600 mt-2">{role.description}</p>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {role.permissions.map((perm, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-sm">
                            <CheckCircle className="w-3 h-3 text-green-600" />
                            {perm}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Security Features */}
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold flex items-center gap-2 mb-4">
                <Lock className="w-5 h-5 text-indigo-600" />
                Authentication
              </h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Microsoft Entra ID (Azure AD) SSO
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Multi-Factor Authentication (MFA) supported
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  OAuth 2.0 Bearer token authentication
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Session timeout enforcement
                </li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold flex items-center gap-2 mb-4">
                <Key className="w-5 h-5 text-indigo-600" />
                Authorization
              </h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Role-based API endpoint protection
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Case-level access restrictions
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Evidence ownership validation
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Action-specific permission checks
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Audit Tab */}
      {activeTab === 'audit' && (
        <div className="space-y-6">
          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-semibold text-lg flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Recent Security Events
              </h2>
              <span className="text-sm text-gray-500">Last 10 events</span>
            </div>
            <div className="divide-y">
              {custodyEvents?.slice(0, 10).map((event: any) => (
                <div key={event.id} className="p-4 flex items-start gap-4">
                  <div className={`p-2 rounded-full ${
                    event.eventType === 'uploaded' || event.eventType === 'created' ? 'bg-green-100' :
                    event.eventType === 'downloaded' || event.eventType === 'viewed' ? 'bg-blue-100' :
                    event.eventType === 'modified' || event.eventType === 'deleted' ? 'bg-yellow-100' :
                    'bg-gray-100'
                  }`}>
                    {event.eventType === 'uploaded' ? <Upload className="w-4 h-4 text-green-600" /> :
                     event.eventType === 'downloaded' ? <Eye className="w-4 h-4 text-blue-600" /> :
                     event.eventType === 'hash_verified' ? <CheckCircle className="w-4 h-4 text-green-600" /> :
                     <FileText className="w-4 h-4 text-gray-600" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{event.eventType}</span>
                      <span className="text-sm text-gray-500">by {event.performedByName || event.performedBy}</span>
                    </div>
                    <p className="text-sm text-gray-600">{event.description}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(event.performedAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              )) || (
                <div className="p-8 text-center text-gray-500">
                  No recent events to display
                </div>
              )}
            </div>
          </div>

          {/* Audit Capabilities */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold mb-4">Audit Trail Capabilities</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-sm text-gray-700">Chain of Custody</h4>
                <p className="text-xs text-gray-500 mt-1">
                  Every evidence action logged with user, timestamp, IP address, and action details
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-sm text-gray-700">Hash Verification</h4>
                <p className="text-xs text-gray-500 mt-1">
                  SHA-256 cryptographic hash computed and stored for all evidence files
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-sm text-gray-700">Immutable Storage</h4>
                <p className="text-xs text-gray-500 mt-1">
                  WORM policies prevent modification or deletion of audit records
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Health Tab */}
      {activeTab === 'health' && (
        <div className="space-y-6">
          {/* System Status */}
          <div className="grid grid-cols-4 gap-6">
            {[
              { name: 'API Gateway', status: 'healthy', icon: Server },
              { name: 'Database', status: 'healthy', icon: Database },
              { name: 'Storage', status: 'healthy', icon: FileText },
              { name: 'Authentication', status: 'healthy', icon: Lock },
            ].map(service => (
              <div key={service.name} className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${
                    service.status === 'healthy' ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    <service.icon className={`w-5 h-5 ${
                      service.status === 'healthy' ? 'text-green-600' : 'text-red-600'
                    }`} />
                  </div>
                  <div>
                    <p className="font-medium">{service.name}</p>
                    <p className={`text-sm ${
                      service.status === 'healthy' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {service.status === 'healthy' ? '● Operational' : '● Degraded'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Custody Events</p>
                  <p className="text-3xl font-bold">{custodyStats?.totalEvents || 0}</p>
                </div>
                <FileText className="w-10 h-10 text-gray-300" />
              </div>
              <p className="text-sm text-green-600 mt-2">
                ✓ Full audit trail maintained
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Cases</p>
                  <p className="text-3xl font-bold">{cases?.length || 0}</p>
                </div>
                <Folder className="w-10 h-10 text-gray-300" />
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Evidence properly associated
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Uptime</p>
                  <p className="text-3xl font-bold">99.9%</p>
                </div>
                <Clock className="w-10 h-10 text-gray-300" />
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Last 30 days
              </p>
            </div>
          </div>

          {/* Azure Services */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Server className="w-5 h-5" />
              Azure Infrastructure
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 border rounded-lg">
                <p className="font-medium">Azure Functions</p>
                <p className="text-sm text-gray-500">func-dems-api-dev (Node.js 20)</p>
              </div>
              <div className="p-3 border rounded-lg">
                <p className="font-medium">Azure SQL Database</p>
                <p className="text-sm text-gray-500">sql-dems-dev (Standard S2)</p>
              </div>
              <div className="p-3 border rounded-lg">
                <p className="font-medium">Blob Storage</p>
                <p className="text-sm text-gray-500">stdemsdev (WORM enabled)</p>
              </div>
              <div className="p-3 border rounded-lg">
                <p className="font-medium">Log Analytics</p>
                <p className="text-sm text-gray-500">law-dems-dev (90-day retention)</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
